import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, ArrowRight, Loader2 } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwaW50ZXJpb3IlMjBmbG9vcmluZyUyMGNhcnBldCUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzgzNjEwNTMyfDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) return;
    setBusy(true);
    try {
      const user = await login(identifier.trim(), password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      navigate(user.role === "driver" ? "/my-route" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-stark-brand">
      {/* Left hero */}
      <div className="relative hidden w-1/2 lg:block">
        <img src={HERO} alt="Premium flooring" className="h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(43,45,43,0.55) 0%, rgba(43,45,43,0.95) 100%)" }} />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/stark-logo-white.png" alt="STARK" className="h-7 w-auto" />
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              The command center for every install & delivery.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/60">
              Live truck tracking, smart ETAs, and delay alerts — keeping ops, account managers,
              and field crews perfectly in sync.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-medium text-white/40">
            <span className="h-2 w-2 rounded-full bg-stark-terracotta" /> Fleet ETA · v1
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full flex-col items-center justify-center px-6 lg:w-1/2">
        <div className="stark-fade-up w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <img src="/stark-logo-white.png" alt="STARK" className="h-7 w-auto" />
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight text-white">Sign in</h2>
          <p className="mt-1.5 text-sm text-white/50">Use your STARK username or email.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-white/50">
                Username or email
              </label>
              <Input
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="ccarpenter"
                autoComplete="username"
                className="h-12 border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-stark-terracotta"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-white/50">
                Password
              </label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="h-12 border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-stark-terracotta"
              />
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="group h-12 w-full rounded-xl bg-stark-terracotta text-[15px] font-bold text-white hover:bg-[#bd4f2c]"
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
